import { removeTrailingZeros } from '@forest/paywall-core'
import { Dialog } from '@kobalte/core/dialog'
import { useStore } from '@nanostores/solid'
import { createEffect, createResource } from 'solid-js'
import { productsReady } from '~/lib/message'
import { m } from '~/paraglide/messages'
import { native } from '@forest/native-services'
import { $chinaAndroidTrialEligibility, $isReminderViewOpen } from '~/store'
import { Bg } from './graphics/bg'
import { ReminderSection } from './reminder-section'

export function ReminderView(props: {
  onStartTrial: () => void
  isLoading?: boolean
}) {
  const isOpen = useStore($isReminderViewOpen)
  const chinaAndroidTrialEligibility = useStore($chinaAndroidTrialEligibility)
  const [products] = createResource(() => productsReady.promise)

  const platform = new URLSearchParams(window.location.search).get('platform')

  const notes = () => {
    if (platform === 'china_android') {
      if (chinaAndroidTrialEligibility() === 'available') {
        return m.onboarding_paywall_footer_trial_cna()
      } else {
        return m.onboarding_paywall_footer_cna()
      }
    }
    const price = removeTrailingZeros(
      products()?.annualEarlyBird.price_localized ?? '',
    )
    return m.onboarding_paywall_footer({
      trial_day_number: 7,
      plan_price: price,
    })
  }

  createEffect(() => {
    if (isOpen()) {
      native.general.logEvent({
        event_name: 'page_view',
        event_params: {
          page_name: 'nu_paywall_trial_timeline_2',
        },
      })
    }
  })

  const handleButtonClick = () => {
    native.general.logEvent({
      event_name: 'component_event',
      event_params: {
        type: 'btn',
        app_action: 'click',
        component_name: 'nu_paywall_trial_timeline_start',
      },
    })
    props.onStartTrial()
  }

  return (
    <Dialog open={isOpen()}>
      <Dialog.Portal>
        <Dialog.Content
          class="fixed inset-0 z-50 bg-[#001721] data-[expanded]:animate-in data-[closed]:animate-out data-[closed]:fade-out-0 data-[expanded]:fade-in-0"
          onOpenAutoFocus={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
        >
          {/* Background */}
          <div class="pointer-events-none absolute left-0 right-0 top-0 h-[900px] overflow-x-hidden bg-top">
            <div class="absolute left-1/2 top-0 h-full -translate-x-1/2">
              <Bg />
            </div>
          </div>

          <div class="relative mx-auto max-w-[640px] space-y-12 px-8 pt-[120px]">
            <h1
              class="whitespace-pre-line px-4 text-center text-2xl font-bold text-white"
              innerHTML={m
                .subscription_walkthrough_p3_title()
                .replace('[%2$s]', 'color: #2AFD00;')}
            />

            <ReminderSection />

            <div class="fixed bottom-0 left-0 right-0 px-6 pb-12 pt-3">
              <div class="mx-auto max-w-[640px] space-y-3">
                <button
                  class="group h-[52px] w-full rounded-xl p-px [background-image:linear-gradient(180deg,#C5FF70_0%,#69FF78_45%,#30D3FF_100%)]"
                  onClick={handleButtonClick}
                  disabled={props.isLoading}
                >
                  <span class="flex h-full w-full items-center justify-center rounded-[11px] font-bold text-[#176361] [background-image:linear-gradient(106deg,#C5FF70_13%,#30D3FF_97%)] group-active:[background-image:linear-gradient(90deg,#94D66E_0%,#2BB8D3_100%)]">
                    {m.btn_try_plus_free()}
                  </span>
                </button>
                <p class="text-center text-xs text-[#FFFFFFE6]">{notes()}</p>
              </div>
            </div>
          </div>

          {/* Close button */}
          <button
            class="absolute left-4 top-[calc(4px+var(--safe-area-top))] size-8"
            onClick={() => {
              $isReminderViewOpen.set(false)
            }}
          >
            <svg
              width="37"
              height="36"
              viewBox="0 0 37 36"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g>
                <rect
                  x="0.75"
                  y="0.75"
                  width="35.5"
                  height="34.5"
                  rx="17.25"
                  fill="white"
                  fill-opacity="0.56"
                />
                <rect
                  x="0.75"
                  y="0.75"
                  width="35.5"
                  height="34.5"
                  rx="17.25"
                  fill="#2C2C2C"
                  style="mix-blend-mode:color-dodge"
                />
                <rect
                  x="0.75"
                  y="0.75"
                  width="35.5"
                  height="34.5"
                  rx="17.25"
                  stroke="url(#paint0_linear_350_2825)"
                  stroke-width="1.5"
                />
                <path
                  d="M20.2143 23.0401L16.0229 18.8487C15.5279 18.3537 15.5279 17.5437 16.0229 17.0487L20.2143 12.8572"
                  stroke="#333333"
                  stroke-width="1.3"
                  stroke-miterlimit="10"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </g>
              <defs>
                <linearGradient
                  id="paint0_linear_350_2825"
                  x1="19.811"
                  y1="-6.75551e-07"
                  x2="29.7414"
                  y2="33.0129"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stop-color="white" stop-opacity="0.3" />
                  <stop offset="1" stop-color="white" stop-opacity="0.1" />
                </linearGradient>
              </defs>
            </svg>
          </button>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  )
}
